import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { CONDITION_TYPES, CONSEQUENCE_TYPES, EDGE_STYLES, NODE_TYPES } from './narrativeConstants';
import { Loader2, Trash2, ArrowRight, Zap, Sparkles, Palette } from 'lucide-react';
import { cn } from '../../lib/utils';

const EMPTY_EDGE = {
  source_id: '',
  target_id: '',
  label: '',
  condition_type: '',
  condition_value: '',
  consequence_type: '',
  consequence_value: '',
  is_active: true,
  style: 'default',
};

export default function NarrativeEdgeEditor({
  open, onOpenChange, edge, nodes = [], language = 'fr', saving, onSave, onDelete,
}) {
  const [form, setForm] = useState(EMPTY_EDGE);
  const lang = language;
  const isEdit = !!edge?.id;

  useEffect(() => {
    if (edge) {
      setForm({
        ...EMPTY_EDGE,
        ...edge,
        condition_type: edge.condition_type === 'none' ? '' : (edge.condition_type || ''),
        consequence_type: edge.consequence_type === 'none' ? '' : (edge.consequence_type || ''),
      });
    } else {
      setForm(EMPTY_EDGE);
    }
  }, [edge, open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.source_id || !form.target_id) return;
    const { id, campaign_id, created_at, ...editableFields } = form;
    if (editableFields.condition_type === 'none') editableFields.condition_type = '';
    if (editableFields.consequence_type === 'none') editableFields.consequence_type = '';
    onSave(editableFields);
  };

  const handleDelete = () => {
    if (edge?.id && onDelete) {
      onDelete(edge.id);
    }
  };

  const sourceNode = nodes.find(n => n.id === form.source_id);
  const targetNode = nodes.find(n => n.id === form.target_id);
  const sourceType = NODE_TYPES[sourceNode?.node_type] || NODE_TYPES.encounter;
  const targetType = NODE_TYPES[targetNode?.node_type] || NODE_TYPES.encounter;
  const edgeStyle = EDGE_STYLES[form.style] || EDGE_STYLES.default;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] sm:max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* ── Header ── */}
        <div className="relative shrink-0">
          <div className="absolute inset-0 opacity-[0.04]" style={{ background: `linear-gradient(135deg, ${edgeStyle.stroke}, transparent 70%)` }} />
          <DialogHeader className="relative px-5 pt-5 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center shrink-0 border border-border/30">
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <DialogTitle className="font-cinzel text-base">
                  {isEdit
                    ? (lang === 'fr' ? 'Modifier la connexion' : 'Edit Connection')
                    : (lang === 'fr' ? 'Nouvelle connexion' : 'New Connection')
                  }
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                  {lang === 'fr' ? 'Définissez les conditions et conséquences de cette transition' : 'Define conditions and consequences of this transition'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-y-auto">
          <div className="px-5 py-4 space-y-5">

            {/* Source → Target visual */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-accent/30 border border-border/30">
              <NodePill node={sourceNode} typeConfig={sourceType} lang={lang} />
              <div className="flex-1 flex items-center justify-center">
                <div className="h-px flex-1 bg-border/60" />
                <ArrowRight className="h-4 w-4 mx-1.5 text-muted-foreground shrink-0" />
                <div className="h-px flex-1 bg-border/60" />
              </div>
              <NodePill node={targetNode} typeConfig={targetType} lang={lang} />
            </div>

            {/* Source → Target selects (when creating) */}
            {!isEdit && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">{lang === 'fr' ? 'Nœud source' : 'Source Node'}</Label>
                  <Select value={form.source_id} onValueChange={v => handleChange('source_id', v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={lang === 'fr' ? 'Sélectionner...' : 'Select...'} /></SelectTrigger>
                    <SelectContent>
                      {nodes.map(n => <SelectItem key={n.id} value={n.id}>{n.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">{lang === 'fr' ? 'Nœud cible' : 'Target Node'}</Label>
                  <Select value={form.target_id} onValueChange={v => handleChange('target_id', v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={lang === 'fr' ? 'Sélectionner...' : 'Select...'} /></SelectTrigger>
                    <SelectContent>
                      {nodes.filter(n => n.id !== form.source_id).map(n => <SelectItem key={n.id} value={n.id}>{n.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Label */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">{lang === 'fr' ? 'Libellé de la transition' : 'Transition Label'}</Label>
              <Input
                value={form.label}
                onChange={e => handleChange('label', e.target.value)}
                placeholder={lang === 'fr' ? 'Ex: Si le groupe tue le capitaine...' : 'Ex: If the group kills the captain...'}
                className="h-9"
              />
            </div>

            {/* Condition */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-amber-500" />
                <Label className="text-xs font-semibold">{lang === 'fr' ? 'Condition' : 'Condition'}</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  value={form.condition_type || 'none'}
                  onValueChange={v => handleChange('condition_type', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={lang === 'fr' ? 'Aucune' : 'None'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{lang === 'fr' ? 'Aucune' : 'None'}</SelectItem>
                    {CONDITION_TYPES.map(ct => (
                      <SelectItem key={ct.value} value={ct.value}>{ct.label[lang]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={form.condition_value}
                  onChange={e => handleChange('condition_value', e.target.value)}
                  placeholder={lang === 'fr' ? 'Détail...' : 'Detail...'}
                  className="h-9"
                />
              </div>
            </div>

            {/* Consequence */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                <Label className="text-xs font-semibold">{lang === 'fr' ? 'Conséquence' : 'Consequence'}</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  value={form.consequence_type || 'none'}
                  onValueChange={v => handleChange('consequence_type', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={lang === 'fr' ? 'Aucune' : 'None'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{lang === 'fr' ? 'Aucune' : 'None'}</SelectItem>
                    {CONSEQUENCE_TYPES.map(ct => (
                      <SelectItem key={ct.value} value={ct.value}>{ct.label[lang]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={form.consequence_value}
                  onChange={e => handleChange('consequence_value', e.target.value)}
                  placeholder={lang === 'fr' ? 'Détail...' : 'Detail...'}
                  className="h-9"
                />
              </div>
            </div>

            {/* Style + Active */}
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-1.5">
                  <Palette className="h-3.5 w-3.5 text-muted-foreground" />
                  <Label className="text-xs font-semibold">{lang === 'fr' ? 'Style visuel' : 'Visual Style'}</Label>
                </div>
                <Select value={form.style} onValueChange={v => handleChange('style', v)}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(EDGE_STYLES).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-0.5 rounded-full" style={{ backgroundColor: cfg.stroke, display: 'inline-block' }} />
                          {cfg.label[lang]}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2.5 pb-1.5 px-3 py-2 rounded-xl border border-border/50 bg-background/50">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={v => handleChange('is_active', v)}
                />
                <Label className="text-xs font-medium">{lang === 'fr' ? 'Active' : 'Active'}</Label>
              </div>
            </div>

            {/* Delete zone */}
            {isEdit && onDelete && (
              <div className="flex items-center justify-between p-3 rounded-xl border border-destructive/15 bg-destructive/5">
                <span className="text-xs text-muted-foreground">
                  {lang === 'fr' ? 'Supprimer cette connexion' : 'Delete this connection'}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  {lang === 'fr' ? 'Supprimer' : 'Delete'}
                </Button>
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <DialogFooter className="px-5 py-3 border-t border-border/40 gap-2 sm:gap-2 mt-auto shrink-0">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-xs">
              {lang === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              disabled={saving || !form.source_id || !form.target_id}
              className="text-xs font-bold gap-1.5 shadow-md"
              style={{ background: edgeStyle.stroke, color: '#fff' }}
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isEdit
                ? (lang === 'fr' ? 'Enregistrer' : 'Save')
                : (lang === 'fr' ? 'Créer la connexion' : 'Create Connection')
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Node pill for the source → target visual ── */
function NodePill({ node, typeConfig, lang }) {
  if (!node) return <span className="text-[10px] text-muted-foreground px-2">...</span>;
  const Icon = typeConfig.icon;
  return (
    <div className="flex items-center gap-1.5 min-w-0 max-w-[140px]">
      <div
        className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
        style={{ background: typeConfig.color + '15' }}
      >
        <Icon className="h-3 w-3" style={{ color: typeConfig.color }} />
      </div>
      <span className="text-[11px] font-medium truncate">{node.title}</span>
    </div>
  );
}
